import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  Clock,
  Copy,
  Download,
  Flame,
  Instagram,
  Linkedin,
  Twitter,
  MessageCircle,
  Send,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Share2,
  RefreshCw,
  Gift,
} from 'lucide-react';
import { fetchWrapped, type WrappedPlatformSplitItem, type WrappedResponse, fetchSubscription, fetchPostDumpsStatus, type Subscription } from '@/services/api';
import { Button } from '@/components/ui/button';
import * as htmlToImage from 'html-to-image';
import { TikTokIcon } from '@/components/TikTokIcon';
import { RedditIcon } from '@/components/RedditIcon';

const YEAR = 2025;

type SlideKey =
  | 'cover'
  | 'total_saves'
  | 'category_dna'
  | 'platform_mix'
  | 'taste_shift'
  | 'longest_streak'
  | 'weeks_active'
  | 'peak_save_time'
  | 'favourite_creator'
  | 'time_saved_and_revisit'
  | 'biggest_rabbit_hole'
  | 'persona';

type SlideSpec = {
  key: SlideKey;
  durationMs: number;
};

type ShareAllCardsFn = () => Promise<void>;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function filterCaptureNode(n: HTMLElement) {
  // Exclude share footer and any explicitly excluded nodes
  if (n.getAttribute?.('data-capture-exclude') === 'true') return false;

  // Avoid tainted canvas: exclude cross-origin images during capture.
  if (n.tagName === 'IMG') {
    const imgEl = n as HTMLImageElement;
    const src = imgEl.currentSrc || imgEl.src || '';
    if (src && isCrossOriginUrl(src)) return false;
  }
  return true;
}

async function withStylesheetAccessWorkaround<T>(fn: () => Promise<T>): Promise<T> {
  // html-to-image tries to read document.styleSheets[].cssRules, which throws for cross-origin sheets.
  // Workaround: temporarily disable those stylesheets during capture.
  const sheets = Array.from(document.styleSheets || []);
  const disabled: CSSStyleSheet[] = [];
  let crossOriginCount = 0;
  for (const sheet of sheets) {
    try {
      // Accessing cssRules throws SecurityError for cross-origin sheets
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      sheet.cssRules;
    } catch {
      crossOriginCount += 1;
      try {
        // @ts-ignore
        if (!sheet.disabled) {
          // @ts-ignore
          sheet.disabled = true;
          disabled.push(sheet);
        }
      } catch {
        // ignore
      }
    }
  }

  try {
    return await fn();
  } finally {
    for (const sheet of disabled) {
      try {
        // @ts-ignore
        sheet.disabled = false;
      } catch {
        // ignore
      }
    }
  }
}

function isCrossOriginUrl(url: string) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

async function withCaptureMode<T>(fn: () => Promise<T>): Promise<T> {
  try {
    document.documentElement.setAttribute('data-iv-capture', '1');
  } catch {
    // ignore
  }
  try {
    // Give the browser a moment to apply capture-only CSS (prevents “overlay/dim” captures).
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    return await fn();
  } finally {
    try {
      document.documentElement.removeAttribute('data-iv-capture');
    } catch {
      // ignore
    }
  }
}

async function withCaptureSquareCorners<T>(fn: () => Promise<T>): Promise<T> {
  try {
    document.documentElement.setAttribute('data-iv-capture-square', '1');
  } catch {
    // ignore
  }
  try {
    // Let CSS apply
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    return await fn();
  } finally {
    try {
      document.documentElement.removeAttribute('data-iv-capture-square');
    } catch {
      // ignore
    }
  }
}

function formatDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCompactNumber(n: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact' }).format(n);
}

function isoWeekNumber(isoDate: string) {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  // ISO week date weeks start on Monday, week 1 contains Jan 4th.
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
}

function buildMarketingShareText(data: WrappedResponse) {
  const total = data.totals?.total_saves ?? 0;
  return `Here's my saved findings — ${total} saves in ${data.year}. Find out yours on Instavault`;
}

async function dataUrlToFile(dataUrl: string, fileName: string) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/png' });
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ShareFooter({
  data,
  captureTargetRef,
  onBusyChange,
  onRequestPause,
}: {
  data: WrappedResponse;
  captureTargetRef: React.RefObject<HTMLElement>;
  onBusyChange?: (busy: boolean) => void;
  onRequestPause?: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const shareText = useMemo(() => buildMarketingShareText(data), [data]);
  const shareUrl = 'https://instavault.co';
  const fileName = `instavault-rewind-${data.year}.png`;

  const isProbablyMobile = () => {
    // Prefer userAgentData if present
    // @ts-ignore
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      // @ts-ignore
      return navigator.userAgentData.mobile;
    }
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
  };

  // Single screenshot (download/share footer) should be full-bleed: no rounded corners.
  const capture = capturePNG(captureTargetRef, { squareCorners: true });

  const tryNativeShare = async (pngDataUrl: string) => {
    // Avoid desktop share sheets (macOS) — only attempt native share on mobile
    // and only if we can share files.
    // @ts-ignore
    if (!navigator.share) return false;
    if (!isProbablyMobile()) return false;
    try {
      const file = await dataUrlToFile(pngDataUrl, fileName);
      // @ts-ignore
      if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
        return false;
      }
      // @ts-ignore
      await navigator.share({
        title: `Instavault Rewind ${data.year}`,
        text: shareText,
        files: [file],
      });
      return true;
    } catch {
      return false;
    }
  };

  const openIntent = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const withScreenshot = async (after: (png: string) => Promise<void> | void) => {
    if (busy) return;
    onRequestPause?.();
    setBusy(true);
    onBusyChange?.(true);
    try {
      const png = await capture();
      await after(png);
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  const onShareX = () =>
    withScreenshot(async (png) => {
      const shared = await tryNativeShare(png);
      if (!shared) downloadDataUrl(png, fileName);
      const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      openIntent(intent);
    });

  const onShareLinkedIn = () =>
    withScreenshot(async (png) => {
      const shared = await tryNativeShare(png);
      if (!shared) downloadDataUrl(png, fileName);
      const intent = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      try {
        await navigator.clipboard.writeText(shareText);
      } catch {
        // ignore
      }
      openIntent(intent);
    });

  const onShareWhatsApp = () =>
    withScreenshot(async (png) => {
      const shared = await tryNativeShare(png);
      if (!shared) downloadDataUrl(png, fileName);
      const intent = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
      openIntent(intent);
    });

  const onShareTelegram = () =>
    withScreenshot(async (png) => {
      const shared = await tryNativeShare(png);
      if (!shared) downloadDataUrl(png, fileName);
      const intent = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
      openIntent(intent);
    });

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    } catch {
      // ignore
    }
  };

  const onDownload = () =>
    withScreenshot(async (png) => {
      downloadDataUrl(png, fileName);
    });

  return (
    <div
      data-wrapped-interactive="true"
      data-capture-exclude="true"
      className="wrapped-share-footer border-t border-white/10 bg-black/30 backdrop-blur-xl px-4 py-3"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="text-xs text-white/75 font-medium">Share on social profile</div>
          <div className="text-[11px] text-white/55 truncate">
            {busy ? 'Preparing screenshot…' : `Caption: “Find out yours on Instavault`}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-between sm:justify-end">
          <button
            onClick={onShareX}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Share on X"
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition pointer-events-none rounded-full bg-gradient-to-br from-white/10 to-white/0" />
            <Twitter style={{ width: 16, height: 16 }} className="h-4 w-4 text-white/90 block" />
          </button>
          <button
            onClick={onShareLinkedIn}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Share on LinkedIn"
          >
            <Linkedin style={{ width: 16, height: 16 }} className="h-4 w-4 text-sky-200 block" />
          </button>
          <button
            onClick={onShareWhatsApp}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Share on WhatsApp"
          >
            <MessageCircle style={{ width: 16, height: 16 }} className="h-4 w-4 text-emerald-200 block" />
          </button>
          <button
            onClick={onShareTelegram}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Share on Telegram"
          >
            <Send style={{ width: 16, height: 16 }} className="h-4 w-4 text-sky-200 block" />
          </button>
          <div className="h-6 w-px bg-white/12 mx-1" />
          <button
            onClick={onCopy}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Copy caption"
          >
            <Copy style={{ width: 16, height: 16 }} className="h-4 w-4 text-white/85 block" />
          </button>
          <button
            onClick={onDownload}
            disabled={busy}
            className="group relative h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/12 inline-flex items-center justify-center transition disabled:opacity-60"
            title="Download this card"
          >
            <Download style={{ width: 16, height: 16 }} className="h-4 w-4 text-white/85 block" />
          </button>
        </div>
      </div>

    </div>
  );
}

function capturePNG<T extends HTMLElement>(
  captureTargetRef: React.RefObject<T>,
  opts?: { squareCorners?: boolean; includeBranding?: boolean }
) {
  // Captures ONLY the currently rendered slide root:
  // <div data-wrapped-slide-key="..."> ... </div>
  return async (): Promise<string> => {
    const card = captureTargetRef.current;
    if (!card) throw new Error('Nothing to capture');
    const slideEl = card.querySelector('[data-wrapped-slide-key]') as HTMLElement | null;
    if (!slideEl) throw new Error('No slide root found to capture');

    return await withCaptureMode(async () => {
      const injectBranding = () => {
        if (opts?.includeBranding === false) {
          return () => {};
        }
        // Add Instavault branding to the *captured* PNG (single-card),
        // similar to the collage footer strip, without affecting live UI.
        const brand = document.createElement('div');
        brand.setAttribute('data-iv-capture-branding', 'true');
        brand.style.position = 'absolute';
        brand.style.left = '0';
        brand.style.right = '0';
        brand.style.bottom = '0';
        brand.style.padding = '14px 16px';
        brand.style.background = 'rgba(0,0,0,0.35)';
        brand.style.color = 'rgba(255,255,255,0.92)';
        brand.style.fontFamily = 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        brand.style.fontWeight = '700';
        brand.style.fontSize = '16px';
        brand.style.letterSpacing = '0.2px';
        brand.style.textAlign = 'center';
        brand.style.pointerEvents = 'none';
        brand.style.zIndex = '9999';
        brand.textContent = `Instavault Rewind ${YEAR} • instavault.co`;

        const prevPos = slideEl.style.position;
        const prevOverflow = slideEl.style.overflow;
        // Ensure absolute positioning anchors correctly.
        if (!prevPos || prevPos === 'static') {
          slideEl.style.position = 'relative';
        }
        // Keep branding inside the captured bounds.
        slideEl.style.overflow = 'hidden';
        slideEl.appendChild(brand);

        return () => {
          try {
            brand.remove();
          } catch {
            // ignore
          }
          slideEl.style.position = prevPos;
          slideEl.style.overflow = prevOverflow;
        };
      };

      const run = async () =>
        await withStylesheetAccessWorkaround(async () => {
          const cleanupBranding = injectBranding();
          try {
            return await htmlToImage.toPng(slideEl, {
              cacheBust: true,
              pixelRatio: 2,
              // Let the element's own background render (capture-mode CSS injects it)
              backgroundColor: 'transparent',
              filter: (n) => {
                try {
                  return filterCaptureNode(n as HTMLElement);
                } catch {
                  return true;
                }
              },
            });
          } finally {
            cleanupBranding();
          }
        });

      if (opts?.squareCorners) {
        return await withCaptureSquareCorners(run);
      }
      return await run();
    });
  };
}

function platformLabel(platform: WrappedPlatformSplitItem['platform']) {
  switch (platform) {
    case 'INSTAGRAM':
      return 'Instagram';
    case 'X':
      return 'X';
    case 'LINKEDIN':
      return 'LinkedIn';
    case 'TIKTOK':
      return 'TikTok';
    case 'REDDIT':
      return 'Reddit';
    default:
      return String(platform);
  }
}

function platformAccent(platform: WrappedPlatformSplitItem['platform']) {
  switch (platform) {
    case 'INSTAGRAM':
      return 'from-pink-500 to-purple-600';
    case 'X':
      return 'from-zinc-300 to-zinc-100';
    case 'LINKEDIN':
      return 'from-sky-500 to-blue-700';
    case 'TIKTOK':
      return 'from-cyan-400 via-pink-500 to-red-500';
    case 'REDDIT':
      return 'from-orange-500 to-red-600';
    default:
      return 'from-purple-500 to-fuchsia-500';
  }
}

function hourEmoji(h: number) {
  if (h >= 5 && h <= 10) return 'AM';
  if (h >= 11 && h <= 15) return 'MIDDAY';
  if (h >= 16 && h <= 19) return 'EVENING';
  if (h >= 20 || h <= 1) return 'NIGHT';
  return 'LATE';
}

function computeProgressSegments(activeIndex: number, progress01: number, total: number) {
  return Array.from({ length: total }).map((_, idx) => {
    if (idx < activeIndex) return 1;
    if (idx > activeIndex) return 0;
    return progress01;
  });
}

function ConfettiBurst({
  burstKey,
  pieces = 46,
}: {
  burstKey: string | number;
  pieces?: number;
}) {
  const colors = [
    'bg-pink-400',
    'bg-purple-400',
    'bg-sky-300',
    'bg-emerald-300',
    'bg-amber-300',
    'bg-fuchsia-300',
  ];

  // Deterministic distribution across full width (prevents “right-heavy” feel).
  const items = Array.from({ length: pieces }).map((_, i) => {
    const seed = (String(burstKey).length * 997 + i * 37) % 1000;
    const base = pieces <= 1 ? 50 : (i / (pieces - 1)) * 100; // 0..100 evenly
    const jitter = -6 + ((seed * 11) % 13); // -6..6
    const left = clamp(base + jitter, 0, 100);
    const delay = ((seed % 70) / 100) * 0.45; // 0..0.315
    const dur = 1.25 + ((seed % 100) / 100) * 0.9; // 1.25..2.15
    const drift = -36 + ((seed * 17) % 72); // -36..36
    const rot = -220 + ((seed * 13) % 440); // -220..220
    const sizeW = 6 + (seed % 6); // 6..11
    const sizeH = 8 + ((seed * 7) % 10); // 8..17
    const color = colors[seed % colors.length];
    const shape = seed % 3; // 0,1,2
    return { i, left, delay, dur, drift, rot, sizeW, sizeH, color, shape };
  });

  return (
    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
      {items.map((p) => (
        <motion.div
          key={`${burstKey}-${p.i}`}
          className={`absolute top-[-14px] ${p.color} ${p.shape === 0 ? 'rounded-sm' : p.shape === 1 ? 'rounded-full' : 'rounded-[2px]'}`}
          style={{
            left: `${p.left}%`,
            width: `${p.sizeW}px`,
            height: `${p.sizeH}px`,
            filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.25))',
          }}
          initial={{ y: -30, x: 0, rotate: 0, opacity: 0 }}
          animate={{
            // Overshoot typical story-card heights so confetti reaches the bottom on tall screens.
            y: [-20, 980],
            x: [0, p.drift],
            rotate: [0, p.rot],
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            delay: p.delay,
            duration: p.dur,
            ease: 'easeOut',
            times: [0, 0.08, 0.72, 1],
          }}
        />
      ))}

      {/* second lighter layer for “whole card” fill */}
      {items.slice(0, Math.floor(pieces * 0.5)).map((p) => (
        <motion.div
          key={`${burstKey}-l2-${p.i}`}
          className={`absolute top-[-18px] ${p.color} opacity-70 ${p.shape === 0 ? 'rounded-sm' : p.shape === 1 ? 'rounded-full' : 'rounded-[2px]'}`}
          style={{
            left: `${(p.left + 7) % 100}%`,
            width: `${Math.max(4, p.sizeW - 2)}px`,
            height: `${Math.max(6, p.sizeH - 4)}px`,
            filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.18))',
          }}
          initial={{ y: -36, x: 0, rotate: 0, opacity: 0 }}
          animate={{
            y: [-24, 1040],
            x: [0, p.drift * 1.35],
            rotate: [0, p.rot * 1.25],
            opacity: [0, 0.9, 0.9, 0],
          }}
          transition={{
            delay: p.delay + 0.06,
            duration: p.dur + 0.35,
            ease: 'easeOut',
            times: [0, 0.08, 0.72, 1],
          }}
        />
      ))}
      {/* soft glow pop */}
      <motion.div
        key={`glow-${burstKey}`}
        className="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 h-56 w-56 rounded-full bg-gradient-to-br from-purple-500/25 via-pink-500/15 to-sky-400/15 blur-2xl"
        initial={{ opacity: 0, scale: 0.75 }}
        animate={{ opacity: [0, 1, 0.4], scale: [0.75, 1.05, 1] }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
      />
    </div>
  );
}

function RadialPct({
  pct,
  label,
  subtitle,
  accent,
}: {
  pct: number;
  label: string;
  subtitle: string;
  accent: string;
}) {
  const size = 96;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamp(pct, 0, 100) / 100) * c;
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={stroke}
            fill="transparent"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="url(#grad)"
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="transparent"
            strokeDasharray={`${dash} ${c - dash}`}
            initial={{ strokeDasharray: `0 ${c}` }}
            animate={{ strokeDasharray: `${dash} ${c - dash}` }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="text-xl font-bold text-white">{Math.round(pct)}%</div>
            <div className="text-[10px] text-white/60 -mt-0.5">share</div>
          </div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className={`h-2.5 w-2.5 rounded-full bg-gradient-to-r ${accent}`} />
          <div className="text-white font-semibold truncate">{label}</div>
        </div>
        <div className="text-white/60 text-sm truncate">{subtitle}</div>
      </div>
    </div>
  );
}

export default function WrappedPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const baseSlides: SlideSpec[] = useMemo(
    () => [
      { key: 'cover', durationMs: 5200 },
      { key: 'total_saves', durationMs: 6500 },
      { key: 'category_dna', durationMs: 7200 },
      { key: 'platform_mix', durationMs: 7600 },
      { key: 'taste_shift', durationMs: 7200 },
      { key: 'longest_streak', durationMs: 6800 },
      { key: 'weeks_active', durationMs: 7600 },
      { key: 'peak_save_time', durationMs: 6600 },
      { key: 'favourite_creator', durationMs: 7600 },
      { key: 'time_saved_and_revisit', durationMs: 8600 },
      { key: 'biggest_rabbit_hole', durationMs: 7600 },
      { key: 'persona', durationMs: 9200 },
    ],
    []
  );

  const [data, setData] = useState<WrappedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [noPosts, setNoPosts] = useState(false);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressActiveRef = useRef(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [postDumpsStatus, setPostDumpsStatus] = useState<{ max_posts_limit: number; current_posts_count?: number; unprocessed_post_dumps_count: number } | null>(null);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const elapsedRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shareCardRef = useRef<HTMLDivElement | null>(null);
  const [progress01, setProgress01] = useState(0);

  const slides: SlideSpec[] = useMemo(() => {
    // If wrapped isn't available or still loading, keep a stable deck.
    if (loading || !data?.has_wrapped) return baseSlides;

    // Hide Biggest Rabbit Hole card when data is not available.
    const rabbitHole = data.biggest_rabbit_hole;
    const hasRabbitHole =
      !!rabbitHole &&
      !!rabbitHole.category_display_value &&
      (rabbitHole.total_count ?? 0) > 0 &&
      Array.isArray(rabbitHole.series) &&
      rabbitHole.series.length > 0;

    return hasRabbitHole ? baseSlides : baseSlides.filter((s) => s.key !== 'biggest_rabbit_hole');
  }, [baseSlides, data, loading]);

  const total = slides.length;
  const activeSlide = slides[clamp(active, 0, total - 1)];
  const duration = activeSlide.durationMs;
  const segments = computeProgressSegments(active, progress01, total);

  const goTo = (idx: number) => {
    const next = clamp(idx, 0, total - 1);
    setActive(next);
    elapsedRef.current = 0;
    setProgress01(0);
    startedAtRef.current = performance.now();
  };

  const isProbablyMobile = () => {
    // Prefer userAgentData if present
    // @ts-ignore
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      // @ts-ignore
      return navigator.userAgentData.mobile;
    }
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
  };

  const tryNativeShareFile = async (pngDataUrl: string, fileName: string, text: string) => {
    // @ts-ignore
    if (!navigator.share) return false;
    if (!isProbablyMobile()) return false;
    try {
      const file = await dataUrlToFile(pngDataUrl, fileName);
      // @ts-ignore
      if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
        return false;
      }
      // @ts-ignore
      await navigator.share({
        title: `Instavault Rewind ${YEAR}`,
        text,
        files: [file],
      });
      return true;
    } catch {
      return false;
    }
  };

  const raf2 = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const getRenderedSlideKey = () => {
    const node = shareCardRef.current;
    if (!node) return null;
    const el = node.querySelector('[data-wrapped-slide-key]') as HTMLElement | null;
    return el?.getAttribute('data-wrapped-slide-key') || null;
  };

  const waitForRenderedSlideKey = async (expected: string, timeoutMs = 2500) => {
    const start = performance.now();
    while (performance.now() - start < timeoutMs) {
      const k = getRenderedSlideKey();
      if (k === expected) return true;
      await raf2();
    }
    return false;
  };

  const dataUrlToImage = (dataUrl: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

  const shareAllCards: ShareAllCardsFn = async () => {
    if (!data?.has_wrapped) return;

    const wasPaused = paused;
    const originalActive = active;

    setPaused(true);
    setShareBusy(true);
    try {
      // Capture each slide behind the overlay (no flicker for user).
      const captured: string[] = [];
      for (let i = 0; i < slides.length; i++) {
        const intendedKey = slides[i]?.key || null;
        goTo(i);
        const ok = intendedKey ? await waitForRenderedSlideKey(intendedKey, 3500) : false;
        const renderedKey = getRenderedSlideKey();
        void ok;
        void renderedKey;
        // Let any in-card animations finish so we capture final values.
        // (Capture mode freezes CSS transitions, but framer-motion updates can still take a moment.)
        await raf2();
        await sleep(1500);
        // Collage should keep the rounded corners look
        // Collage gets a single shared branding strip on the final canvas,
        // so don't burn branding into each individual captured slide.
        const png = await capturePNG(shareCardRef, { squareCorners: false, includeBranding: false })();
        captured.push(png);
      }

      // Restore original slide before we share (UI consistency).
      goTo(originalActive);

      // Build a clean grid collage.
      const imgs = await Promise.all(captured.map(dataUrlToImage));
      const tileW = imgs[0]?.naturalWidth || imgs[0]?.width || 1000;
      const tileH = imgs[0]?.naturalHeight || imgs[0]?.height || 1600;
      const cols = Math.min(3, imgs.length);
      const rows = Math.ceil(imgs.length / cols);
      const gap = Math.round(tileW * 0.03); // ~3% spacing
      const pad = Math.round(tileW * 0.04); // ~4% padding

      const canvas = document.createElement('canvas');
      canvas.width = pad * 2 + cols * tileW + (cols - 1) * gap;
      canvas.height = pad * 2 + rows * tileH + (rows - 1) * gap;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not available');

      // Background
      ctx.fillStyle = '#06060A';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw tiles
      imgs.forEach((img, idx) => {
        const r = Math.floor(idx / cols);
        const c = idx % cols;
        const x = pad + c * (tileW + gap);
        const y = pad + r * (tileH + gap);
        ctx.drawImage(img, x, y, tileW, tileH);
      });

      // Minimal branding strip (subtle)
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, canvas.height - Math.round(tileW * 0.12), canvas.width, Math.round(tileW * 0.12));
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = `bold ${Math.round(tileW * 0.05)}px Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`Instavault Rewind ${YEAR} • instavault.co`, canvas.width / 2, canvas.height - Math.round(tileW * 0.045));

      const collage = canvas.toDataURL('image/png');
      const fileName = `instavault-rewind-${YEAR}-all-cards.png`;
      const text = `Here's my saved findings. Find out yours on Instavault`;

      const shared = await tryNativeShareFile(collage, fileName, text);
      if (!shared) {
        downloadDataUrl(collage, fileName);
      }
    } finally {
      setShareBusy(false);
      setPaused(wasPaused);
    }
  };

  // If deck size changes (e.g., rabbit hole removed), keep active index valid.
  useEffect(() => {
    if (active > total - 1) {
      goTo(total - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const next = () => {
    if (active >= total - 1) return;
    goTo(active + 1);
  };

  const prev = () => {
    if (active <= 0) return;
    goTo(active - 1);
  };

  const exit = () => {
    navigate('/');
  };

  const retry = async () => {
    setLoading(true);
    setLoadError(null);
    setNoPosts(false);
    try {
      const res = await fetchWrapped(YEAR);
      setData(res);
      setLoading(false);
      if (!res.has_wrapped) {
        // keep the user on the page with an empty state
        return;
      }
      goTo(0);
    } catch (e: any) {
      // Backend may throw when there are no posts; treat that as a friendly empty state.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = String(detail || e?.message || '');
      const isNoPosts =
        status === 404 ||
        status === 204 ||
        /no\s+posts|no\s+saves|no\s+saved/i.test(msg);

      if (isNoPosts) {
        setData(null);
        setLoadError(null);
        setNoPosts(true);
        setLoading(false);
        return;
      }

      setLoadError(e?.message || 'Failed to load rewind.');
      setLoading(false);
    }
  };

  useEffect(() => {
    retry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch subscription and post dumps status for upgrade banner
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        const [subscriptionData, postStatus] = await Promise.all([
          fetchSubscription().catch(() => null),
          fetchPostDumpsStatus().catch(() => null),
        ]);
        setSubscription(subscriptionData);
        setPostDumpsStatus(postStatus);
      } catch {
        // Ignore errors
      }
    };
    loadSubscriptionData();
  }, []);

  // Determine if upgrade banner should be shown
  useEffect(() => {
    // Need postDumpsStatus to check unprocessed count
    if (!postDumpsStatus) {
      setShowUpgradeBanner(false);
      return;
    }

    const premiumUntil = localStorage.getItem('premiumUntil');
    const activeSubscriptionStatuses = ['active', 'trialing'];
    
    // No subscription means free plan
    // Check if user has active subscription
    const hasActiveSubscription = subscription && activeSubscriptionStatuses.includes(subscription.status);
    
    // Check if premiumUntil is valid
    const isPremiumUntilValid = premiumUntil &&
      premiumUntil !== 'null' &&
      premiumUntil !== 'undefined' &&
      new Date(premiumUntil) > new Date();
    
    // User is premium only if they have both active subscription AND valid premiumUntil
    const isPremium = hasActiveSubscription && isPremiumUntilValid;

    // Show banner only when:
    // 1. There are unprocessed post dumps (unprocessed_post_dumps_count > 0)
    // 2. User is not premium (including no subscription = free plan)
    const shouldShow = postDumpsStatus.unprocessed_post_dumps_count > 0 && !isPremium;
    setShowUpgradeBanner(shouldShow);
  }, [subscription, postDumpsStatus]);

  useEffect(() => {
    startedAtRef.current = performance.now();
    const tick = (t: number) => {
      if (!paused && !loading && data?.has_wrapped) {
        const base = startedAtRef.current || t;
        elapsedRef.current = t - base;
        // Force re-render so the top progress bar animates smoothly.
        setProgress01(clamp(duration === 0 ? 0 : elapsedRef.current / duration, 0, 1));
        if (!reduceMotion && elapsedRef.current >= duration) {
          if (active < total - 1) {
            goTo(active + 1);
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, data?.has_wrapped, duration, loading, paused, reduceMotion, total]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exit();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === ' ') setPaused((p) => !p);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, total]);

  const onTap = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    if (x < 0.33) {
      if (active <= 0) return;
      prev();
      return;
    }
    if (active >= total - 1) return;
    next();
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const bg = (
    <>
      <div className="absolute inset-0 bg-[#06060A]" />
      <div className="absolute inset-0 opacity-80 bg-[radial-gradient(1200px_600px_at_20%_10%,rgba(168,85,247,0.35),transparent),radial-gradient(900px_520px_at_90%_30%,rgba(236,72,153,0.25),transparent),radial-gradient(700px_560px_at_40%_90%,rgba(59,130,246,0.18),transparent)]" />
      <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] bg-[size:28px_28px]" />
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl bg-purple-500/25" />
      <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full blur-3xl bg-pink-500/20" />
    </>
  );

  return (
    <div className="fixed inset-0 z-[80] text-white">
      {bg}
      <div className="absolute inset-0" ref={containerRef} />

      {/* Upgrade Banner */}
      {showUpgradeBanner && postDumpsStatus && (
        <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-r from-purple-600/95 to-pink-600/95 backdrop-blur-sm border-b border-white/20 px-4 py-3">
          <div className="max-w-full px-6 mx-auto flex items-center justify-between gap-4">
              <div className="text-sm font-medium text-white">
                You are only seeing data from your {postDumpsStatus.current_posts_count} saved posts.{' '}
                <span className="font-semibold">Upgrade now</span> to get data from your all posts.
            </div>
            <Button
              onClick={() => navigate('/profile?active=subscription')}
              className="bg-white text-purple-600 hover:bg-white/90 font-semibold px-4 py-2 whitespace-nowrap flex-shrink-0"
            >
              Upgrade now
            </Button>
          </div>
        </div>
      )}

      {/* Top progress segments */}
      {!loading && !loadError && !noPosts && data?.has_wrapped && (
        <div className={`absolute top-0 left-0 right-0 z-20 p-3 sm:p-4 ${showUpgradeBanner ? 'pt-[max(60px,env(safe-area-inset-top)+48px)]' : 'pt-[max(12px,env(safe-area-inset-top))]'}`}>
          <div className="flex items-center gap-2">
            {segments.map((v, idx) => (
              <div key={idx} className="h-1.5 flex-1 rounded-full bg-white/15 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-white"
                  initial={false}
                  animate={{ width: `${Math.round(v * 100)}%` }}
                  transition={{ duration: reduceMotion ? 0 : 0.12, ease: 'linear' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header controls */}
      <div className={`absolute ${showUpgradeBanner ? 'top-[88px] sm:top-[96px]' : 'top-[48px] sm:top-[56px]'} left-0 right-0 z-20 px-3 sm:px-4 flex items-center justify-between`}>
        <button
          onClick={exit}
          disabled={shareBusy}
          data-wrapped-interactive="true"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

      </div>

      {/* Main story surface */}
      <div
        className={`absolute inset-0 z-10 ${showUpgradeBanner ? 'pt-[144px] sm:pt-[156px]' : 'pt-[104px] sm:pt-[116px]'} pb-[max(16px,env(safe-area-inset-bottom))] px-3 sm:px-4`}
        onPointerDown={(e) => {
          // Long-press to pause (don’t pause on quick taps).
          // Also ignore if clicking any interactive element.
          const target = e.target as HTMLElement | null;
          if (target?.closest?.('[data-wrapped-interactive="true"]')) return;

          longPressActiveRef.current = false;
          clearLongPressTimer();
          longPressTimerRef.current = window.setTimeout(() => {
            longPressActiveRef.current = true;
            setPaused(true);
          }, 260);
        }}
        onPointerUp={() => {
          clearLongPressTimer();
          if (longPressActiveRef.current) {
            longPressActiveRef.current = false;
            setPaused(false);
          }
        }}
        onPointerCancel={() => {
          clearLongPressTimer();
          if (longPressActiveRef.current) {
            longPressActiveRef.current = false;
            setPaused(false);
          }
        }}
        onPointerLeave={() => {
          clearLongPressTimer();
          if (longPressActiveRef.current) {
            longPressActiveRef.current = false;
            setPaused(false);
          }
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement | null;
          // If user clicked a button/link/share footer, don't navigate slides.
          if (target?.closest?.('[data-wrapped-interactive="true"]')) return;
          // If this click was actually a long-press release, don't navigate.
          if (longPressActiveRef.current) return;
          onTap((e as any).clientX);
        }}
      >
        {shareBusy && (
          <div
            data-wrapped-interactive="true"
            className="wrapped-capture-overlay fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center"
          >
            <div className="rounded-2xl bg-white/10 border border-white/15 px-5 py-4 text-center max-w-xs">
              <div className="mx-auto h-8 w-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
              <div className="mt-3 font-semibold">Preparing screenshot…</div>
              <div className="mt-1 text-xs text-white/60">This usually takes few moments.</div>
            </div>
          </div>
        )}
        <div className="mx-auto max-w-[520px] h-full">
          <div
            ref={shareCardRef}
            className="wrapped-capture-card h-full rounded-[28px] border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_20px_100px_rgba(0,0,0,0.55)] overflow-hidden relative flex flex-col"
          >
            {/* subtle inner border */}
            <div className="absolute inset-0 pointer-events-none border border-white/10 rounded-[28px]" />

            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="mb-12 h-full flex flex-col">
                <AnimatePresence mode="wait">
                  {loading ? (
                    <motion.div
                      key="loading"
                      className="h-full p-6 grid place-items-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="text-center">
                        <div className="inline-flex items-center gap-3 rounded-full bg-white/10 border border-white/10 px-4 py-2">
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Building your rewind…
                        </div>
                        <div className="text-white/60 text-sm mt-3">
                          We’re turning your saves into a story.
                        </div>
                      </div>
                    </motion.div>
                  ) : loadError ? (
                    <motion.div
                      key="error"
                      className="h-full p-6 grid place-items-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="text-center max-w-md">
                        <div className="text-2xl font-bold">Couldn’t load Rewind</div>
                        <div className="text-white/60 mt-2">{loadError}</div>
                        <div className="mt-5 flex items-center justify-center gap-3">
                          <Button variant="outline" className="bg-white/10 border-white/10" onClick={exit}>
                            Go home
                          </Button>
                          <Button onClick={retry} className="bg-purple-600 hover:bg-purple-700">
                            Retry
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ) : noPosts ? (
                    <motion.div
                      key="no-posts"
                      className="h-full p-6 sm:p-8 flex flex-col justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="text-center">
                        <div className="inline-flex items-center gap-2 rounded-full bg-purple-500/15 border border-purple-500/20 px-4 py-2 text-purple-100">
                          <Sparkles className="h-4 w-4" />
                          Instavault Rewind {YEAR}
                        </div>
                        <div className="text-2xl sm:text-3xl font-bold mt-4">No posts saved so far.</div>
                        <div className="text-white/60 mt-2">
                          Import your saves and come back — we’ll turn them into a story.
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-center mt-6">
                        <div className="flex flex-col gap-4 mt-8 w-full max-w-xs">
                          <button
                            data-wrapped-interactive="true"
                            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-2 rounded-full text-lg font-semibold flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl w-full"
                            onClick={() => window.open('https://instagram.com?iv=true', '_blank', 'noopener,noreferrer')}
                          >
                            <Instagram className="mr-2" />
                            Import from Instagram
                          </button>

                          <button
                            data-wrapped-interactive="true"
                            className="bg-black hover:bg-gray-800 text-white px-8 py-2 rounded-full text-lg font-semibold flex items-center justify-center transition-all duration-200 border hover:border-blue shadow-lg hover:shadow-xl w-full"
                            onClick={() => window.open('https://x.com?iv=true', '_blank', 'noopener,noreferrer')}
                          >
                            <div className="w-6 h-6 mr-3 flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                              </svg>
                            </div>
                            Import from X
                          </button>

                          <button
                            data-wrapped-interactive="true"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded-full text-lg font-semibold flex items-center justify-center transition-all duration-200 border hover:border-blue shadow-lg hover:shadow-xl w-full"
                            onClick={() => window.open('https://linkedin.com/feed?iv=true', '_blank', 'noopener,noreferrer')}
                          >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                              <rect width="4" height="12" x="2" y="9"></rect>
                              <circle cx="4" cy="4" r="2"></circle>
                            </svg>
                            Import from LinkedIn
                          </button>

                          <button
                            data-wrapped-interactive="true"
                            className="bg-black hover:bg-black-700 text-white px-8 py-2 rounded-full text-lg font-semibold flex items-center justify-center transition-all duration-200 border hover:border-blue shadow-lg hover:shadow-xl w-full"
                            onClick={() => window.open('https://tiktok.com?iv=true', '_blank', 'noopener,noreferrer')}
                          >
                            <TikTokIcon className="w-5 h-5 mr-2 text-white" />
                            Import from TikTok
                          </button>

                          <button
                            data-wrapped-interactive="true"
                            className="bg-[#FF4500] hover:bg-[#e03d00] text-white px-8 py-2 rounded-full text-lg font-semibold flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl w-full"
                            onClick={() => window.open('https://reddit.com?iv=true', '_blank', 'noopener,noreferrer')}
                          >
                            <RedditIcon className="w-5 h-5 mr-2 text-white" />
                            Import from Reddit
                          </button>
                        </div>
                      </div>

                      <div className="mt-8 flex items-center justify-center">
                        <Button variant="outline" className="bg-white/10 border-white/10" onClick={exit}>
                          Go home
                        </Button>
                      </div>
                    </motion.div>
                  ) : !data?.has_wrapped ? (
                    <motion.div
                      key="empty"
                      className="h-full p-6 grid place-items-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="text-center max-w-md">
                        <div className="inline-flex items-center gap-2 rounded-full bg-purple-500/15 border border-purple-500/20 px-4 py-2 text-purple-100">
                          <Sparkles className="h-4 w-4" />
                          Instavault Rewind {YEAR}
                        </div>
                        <div className="text-2xl font-bold mt-4">No saves yet — no rewind (yet).</div>
                        <div className="text-white/60 mt-2">
                          Save a few posts and come back. We’ll turn them into a story.
                        </div>
                        <div className="mt-5 flex items-center justify-center gap-3">
                          <Button variant="outline" className="bg-white/10 border-white/10" onClick={exit}>
                            Go home
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={slides[active].key}
                      className="h-full"
                      initial={{ opacity: 0, y: 18, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.985 }}
                      transition={{ duration: reduceMotion ? 0 : 0.35, ease: 'easeOut' }}
                    >
                      <StorySlide slideKey={slides[active].key} data={data} onShareAllCards={shareAllCards} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {!loading && !loadError && !noPosts && data?.has_wrapped && (
              <ShareFooter
                data={data}
                captureTargetRef={shareCardRef}
                onBusyChange={setShareBusy}
                onRequestPause={() => setPaused(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* {!loading && !loadError && !noPosts && data?.has_wrapped && (
        <div className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom)+88px)] left-0 right-0 z-20 flex justify-center px-4">
          <div className="text-xs sm:text-sm text-white/70">
            Hold <span className="text-white/90">Space</span> or long-press the card to pause
          </div>
        </div>
      )} */}


    </div>
  );
}

function StorySlide({
  slideKey,
  data,
  onShareAllCards,
}: {
  slideKey: SlideKey;
  data: WrappedResponse;
  onShareAllCards?: ShareAllCardsFn;
}) {
  switch (slideKey) {
    case 'cover':
      return (
        <div className="h-full p-7 sm:p-8 grid place-items-center" data-wrapped-slide-key="cover">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2">
              <Gift className="h-4 w-4" />
              Instavault Rewind
            </div>
            <div className="mt-5 text-5xl sm:text-6xl font-extrabold tracking-tight">
              Rewind <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-sky-200 bg-clip-text text-transparent">{data.year}</span>
            </div>
            <div className="mt-4 text-white/70 text-base sm:text-lg">
              A story built from your saves — from <span className="text-white">{formatDate(data.range.from)}</span> to{' '}
              <span className="text-white">{formatDate(data.range.to)}</span>.
            </div>
            <div className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
              <Zap className="h-4 w-4 text-yellow-200" />
              <span className="text-sm text-white/80">Tap to begin</span>
            </div>
          </div>
        </div>
      );

    case 'total_saves':
      return (
        <div className="min-h-full relative" data-wrapped-slide-key="total_saves">
          {/* Confetti should align to the full card, not the padded content */}
          <ConfettiBurst burstKey={`total-${data.year}-${data.totals.total_saves}`} />

          <div className="min-h-full p-7 sm:p-8 relative z-20 flex flex-col items-center text-center py-12">
             <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-white/85">
              <Sparkles className="h-4 w-4" />
              Total saves
            </div>

            <div className="mt-5 text-2xl sm:text-3xl font-bold text-white">
              Voila! You saved
            </div>

            <motion.div
              className="mt-4 relative"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <div className="absolute inset-0 -z-[1] blur-2xl bg-gradient-to-r from-purple-500/30 via-pink-500/25 to-sky-400/25 rounded-full" />
              <div className="px-6 sm:px-8 py-4 sm:py-5 rounded-[28px] bg-white/6 border border-white/12 backdrop-blur-xl shadow-[0_20px_90px_rgba(0,0,0,0.55)]">
                <div className="text-7xl sm:text-9xl font-extrabold tracking-tight bg-gradient-to-r from-purple-200 via-pink-200 to-sky-100 bg-clip-text text-transparent">
                  {formatCompactNumber(data.totals.total_saves)}
                </div>
              </div>
            </motion.div>

            <div className="mt-5 text-2xl sm:text-3xl font-bold text-white">
              posts this year.
            </div>

            <div className="mt-20 rounded-2xl mb-6 bg-white/5 border border-white/10 px-5 py-4 w-full max-w-[420px]">
              <div className="text-white/70 text-sm">Your saving vibe</div>
              <div className="mt-1 text-white font-semibold">
                {data.totals.total_saves >= 1000
                  ? 'Archive Ace'
                  : data.totals.total_saves >= 700
                    ? 'Power Saver'
                    : data.totals.total_saves >= 500
                      ? 'Mega Curator'
                      : data.totals.total_saves >= 300
                        ? 'Dedicated Selector'
                        : data.totals.total_saves >= 120
                          ? 'Consistent Collector'
                          : data.totals.total_saves >= 50
                            ? 'Emerging Enthusiast'
                            : data.totals.total_saves >= 10
                              ? 'Casual Explorer'
                              : 'Intentional Curator'}
              </div>
            </div>
          </div>
        </div>
      );

    case 'category_dna': {
      const top = data.category_dna_top5 ?? [];
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="category_dna">
          <div className="flex items-center justify-between">
            <div>
              <div className="mt-1 text-2xl sm:text-3xl font-bold">What you couldn’t scroll past?</div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {top.length === 0 ? (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-white/70">
                No categories found for this year.
              </div>
            ) : (
              top.map((c, idx) => (
                <div key={`${c.category_id}-${idx}`} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.category_display_value}</div>
                      <div className="text-xs text-white/60">{formatCompactNumber(c.count)} saves</div>
                    </div>
                    <div className="text-white/80 font-semibold">{Math.round(c.pct)}%</div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-purple-400 via-pink-400 to-sky-300"
                      initial={{ width: 0 }}
                      animate={{ width: `${clamp(c.pct, 0, 100)}%` }}
                      transition={{ duration: 0.9, ease: 'easeOut', delay: 0.05 * idx }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      );
    }

    case 'platform_mix': {
      const mix = (data.platform_mix ?? []).slice().sort((a, b) => b.count - a.count);
      const top = mix.slice(0, 5);
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="platform_mix">
          <div className="mt-1 text-2xl sm:text-3xl font-bold">Where your saves came from?</div>

          <div className={`mt-2 grid gap-3 ${top.length > 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {top.length === 0 ? (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-white/70">
                No platform breakdown available.
              </div>
            ) : (
              top.map((p) => (
                <RadialPct
                  key={String(p.platform)}
                  pct={p.pct}
                  label={platformLabel(p.platform)}
                  subtitle={`${formatCompactNumber(p.count)} saves`}
                  accent={platformAccent(p.platform)}
                />
              ))
            )}
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">Hot take</div>
              <div className="mt-1 text-white font-semibold">
                {top[0] ? `${platformLabel(top[0].platform)} was your home base.` : 'Your platforms were evenly spread.'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'taste_shift': {
      const a = data.taste_shift?.first_half;
      const b = data.taste_shift?.second_half;
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="taste_shift">
          <div className="mt-1 text-2xl sm:text-3xl font-bold">How your taste changed from first half to second half?</div>
          <div className="mt-5 grid grid-cols-1 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-xs text-white/60">Jan–Jun</div>
              <div className="mt-1 text-xl font-bold">{a?.category_display_value || '—'}</div>
              <div className="mt-1 text-white/70 text-sm">
                {a ? `${formatCompactNumber(a.count)} saves (${Math.round(a.pct)}%)` : 'No data'}
              </div>
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-sky-300 to-purple-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${clamp(a?.pct ?? 0, 0, 100)}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-xs text-white/60">Jul–Dec</div>
              <div className="mt-1 text-xl font-bold">{b?.category_display_value || '—'}</div>
              <div className="mt-1 text-white/70 text-sm">
                {b ? `${formatCompactNumber(b.count)} saves (${Math.round(b.pct)}%)` : 'No data'}
              </div>
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-pink-300 to-purple-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${clamp(b?.pct ?? 0, 0, 100)}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 }}
                />
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">Transformation</div>
              <div className="mt-1 text-white font-semibold">
                {a?.category_display_value && b?.category_display_value && a.category_display_value !== b.category_display_value
                  ? `You evolved from “${a.category_display_value}” to “${b.category_display_value}”.`
                  : `Your taste stayed locked in.`}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'longest_streak': {
      const s = data.longest_streak;
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="longest_streak">
          <div className="flex items-center gap-2 text-sm text-white/65">
            <Flame className="h-4 w-4 text-orange-200" />
            Longest streak
          </div>

          <div className="mt-3 text-5xl sm:text-6xl font-extrabold">
            {data.longest_streak_days}
            <span className="text-white/60 text-2xl sm:text-3xl font-bold ml-2">days</span>
          </div>

          <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-white/70 text-sm">Your most consistent run</div>
            <div className="mt-1 font-semibold">
              {s ? `${formatDate(s.from)} → ${formatDate(s.to)}` : 'No streak computed'}
            </div>
            <div className="mt-2 text-xs text-white/55">
              Streak = consecutive days with at least 1 save.
            </div>
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4 flex items-center justify-between">
              <div>
                <div className="text-white/70 text-sm">Streak energy</div>
                <div className="font-semibold">
                  {data.longest_streak_days >= 30 ? 'Unstoppable.' : data.longest_streak_days >= 14 ? 'Locked in.' : 'Warming up.'}
                </div>
              </div>
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-orange-300/20 to-pink-300/10 border border-white/10 grid place-items-center">
                <Flame className="h-5 w-5 text-orange-200" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'weeks_active': {
      const weeks = data.weeks_active?.sparkline_weeks ?? [];
      const max = Math.max(1, data.weeks_active?.max_week_count ?? 1);
      // Filter out zero weeks (user asked: hide empty weeks)
      const nonZeroWeeks = weeks.filter((w) => w.count > 0);
      // Keep the most recent non-zero weeks so the chart remains readable
      const shown = nonZeroWeeks.slice(-52);
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="weeks_active">
          <div className="mt-1 text-2xl sm:text-3xl font-bold">Your saves per week this year</div>

          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-white/70 text-sm mb-3">This year's trend over weeks</div>
            <div className="h-32 flex items-end gap-1">
              {shown.map((w) => {
                const h = clamp((w.count / max) * 100, 0, 100);
                const wk = isoWeekNumber(w.week_start);
                return (
                  <motion.div
                    key={w.week_start}
                    className="flex-1 rounded-t-md bg-gradient-to-t from-purple-500/80 to-pink-400/80"
                    initial={{ height: 2 }}
                    animate={{ height: `${Math.max(2, h)}%` }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                    title={`${wk ? `Week ${wk}` : 'Week'} : ${w.count} posts`}
                  />
                );
              })}
            </div>
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">Pro tip</div>
              <div className="mt-1 text-white font-semibold">
                Your brain loves bursts. Next time you’re on a roll—save first, read later.
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'peak_save_time': {
      const p = data.peak_save_time;
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="peak_save_time">
          <div className="flex items-center gap-2 text-sm text-white/65">
            <Clock className="h-4 w-4 text-sky-200" />
            Peak save time
          </div>

          <div className="mt-4 rounded-3xl bg-gradient-to-br from-white/8 to-white/3 border border-white/10 p-6">
            <div className="text-white/70 text-sm">Your prime saving hour</div>
            <div className="mt-3 flex items-end justify-between gap-4">
              <div>
                <div className="text-5xl sm:text-6xl font-extrabold">
                  {p.label} <span className="text-3xl">{hourEmoji(p.hour_0_23)}</span>
                </div>
                <div className="mt-1 text-white/70 text-sm">
                  {Math.round(p.pct)}% of your saves happened then.
                </div>
              </div>
              <div className="h-14 w-14 rounded-2xl bg-white/10 border border-white/10 grid place-items-center">
                <Clock className="h-6 w-6 text-sky-200" />
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">Diagnosis</div>
              <div className="mt-1 text-white font-semibold">
                You don’t doomscroll. You… curate with intent.
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'favourite_creator': {
      const c = data.favourite_creator;
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="favourite_creator">
          <div className="flex items-center gap-2 text-sm text-white/65">
            <Users className="h-4 w-4 text-pink-200" />
            Favourite creator
          </div>
          <div className="mt-2 text-2xl sm:text-3xl font-bold">Your favourite content creator</div>

          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5">
            {c ? (
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-white/10 border border-white/10 overflow-hidden grid place-items-center">
                  {c.profile_image ? <img src={c.profile_image} alt={c.name} className="h-full w-full object-cover" /> : <Users className="h-6 w-6 text-white/70" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{c.name}</div>
                  <div className="text-white/60 text-sm truncate">
                    {c.username ? `@${c.username}` : '—'} • {c.platform}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-extrabold">{formatCompactNumber(c.post_count)}</div>
                  <div className="text-xs text-white/60">posts saved</div>
                </div>
              </div>
            ) : (
              <div className="text-white/70">No single creator dominated your saves this year.</div>
            )}
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">If you had to pick one voice…</div>
              <div className="mt-1 text-white font-semibold">
                {c ? `It was ${c.name}.` : 'It was a mix of creators.'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'time_saved_and_revisit': {
      const t = data.time_saved_estimate;
      const wr = data.worth_revisiting;
      const minLo = Math.round(t.minutes_range[0]);
      const minHi = Math.round(t.minutes_range[1]);
      const hrsLo = Math.round(t.hours_range[0] * 10) / 10;
      const hrsHi = Math.round(t.hours_range[1] * 10) / 10;
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="time_saved_and_revisit">
          <div className="flex items-center gap-2 text-sm text-white/65">
            <TrendingUp className="h-4 w-4 text-emerald-200" />
            How much time you saved with Instavault?
          </div>
          <div className="mt-2 text-2xl sm:text-3xl font-bold">You bought yourself time</div>

          <div className="mt-6 rounded-3xl bg-gradient-to-br from-emerald-400/15 to-purple-500/10 border border-white/10 p-6">
            <div className="text-white/75 text-sm">Estimated time saved in {data.year}</div>
            <div className="mt-3 text-4xl sm:text-5xl font-extrabold">
              {minLo}–{minHi} min
            </div>
            <div className="text-white/70 text-sm mt-1">
              (≈ {hrsLo}–{hrsHi} hours)
            </div>

          </div>

          <div className="mt-5 rounded-2xl mt-6 bg-white/5 border border-white/10 p-5">
            <div className="text-white/70 text-sm">Worth revisiting</div>
            <div className="mt-1 text-xl font-bold">{formatCompactNumber(wr.count)} shared-assets bookmarks</div>
            <div className="text-white/60 text-sm mt-1">Visit dashboard to see the full list.</div>

            {wr.sample_items?.length ? (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {wr.sample_items.slice(0, 3).map((it) => (
                  <div key={it.post_id} className="aspect-square rounded-xl overflow-hidden bg-white/10 border border-white/10">
                    {it.thumbnail_url ? (
                      <img src={it.thumbnail_url} alt={it.title || 'post'} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-white/60 text-xs px-2 text-center">
                        {it.title || 'Saved item'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    case 'biggest_rabbit_hole': {
      const r = data.biggest_rabbit_hole;
      const series = r?.series ?? [];
      const max = Math.max(1, ...series.map((x) => x.count));
      return (
        <div className="h-full p-7 sm:p-8 flex flex-col" data-wrapped-slide-key="biggest_rabbit_hole">
          <div className="text-sm text-white/65">Biggest rabbit hole</div>
          <div className="mt-1 text-2xl sm:text-3xl font-bold">The category you went deep on</div>

          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{r?.category_display_value || '—'}</div>
                <div className="text-white/60 text-sm">
                  {r ? `${formatCompactNumber(r.total_count)} saves` : 'No spike detected'}
                </div>
              </div>
              {r?.spike_period ? (
                <div className="rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs text-white/70">
                  Spike: {r.spike_period}
                </div>
              ) : null}
            </div>

            {series.length ? (
              <div className="mt-4 h-28 flex items-end gap-1">
                {series.slice(-24).map((p) => {
                  const h = clamp((p.count / max) * 100, 0, 100);
                  return (
                    <motion.div
                      key={p.period}
                      className="flex-1 rounded-t-md bg-gradient-to-t from-pink-500/80 to-purple-400/80"
                      initial={{ height: 2 }}
                      animate={{ height: `${Math.max(2, h)}%` }}
                      transition={{ duration: 0.9, ease: 'easeOut' }}
                      title={`${p.period}: ${p.count}`}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="mt-4 text-white/70 text-sm">No time-series available.</div>
            )}
          </div>

          <div className="mt-auto">
            <div className="rounded-2xl mt-6 bg-white/5 border border-white/10 p-4">
              <div className="text-white/70 text-sm">We see you</div>
              <div className="mt-1 text-white font-semibold">That wasn’t “a phase”. That was research.</div>
            </div>
          </div>
        </div>
      );
    }

    case 'persona': {
      const p = data.persona;

      return (
        <div
          className="h-full p-7 sm:p-8 flex flex-col items-center text-center"
          data-wrapped-slide-key="persona"
        >
          <div className="mt-1 text-2xl sm:text-3xl font-bold">You are</div>

          <div className="mt-6 w-full max-w-xl rounded-3xl bg-gradient-to-br from-purple-500/20 via-pink-500/10 to-sky-400/10 border border-white/10 p-6">
            <div className="text-4xl sm:text-5xl font-extrabold">{p.title}</div>
            <div className="mt-2 text-white/70 text-sm sm:text-base">{p.tagline}</div>
            <div className="mt-5 flex items-center justify-center gap-3">
              <Button
                onClick={async () => {
                  if (onShareAllCards) {
                    await onShareAllCards();
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(p.share_text);
                  } catch {
                    // ignore
                  }
                }}
                className="bg-white text-black hover:bg-white/90"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
            <div className="text-xs mt-6 text-white/55">Share your Instavault wrapped (no private data - even post images are not included).</div>
          </div>

          
        </div>
      );
    }
    default:
      return (
        <div className="h-full p-7 sm:p-8 grid place-items-center">
          <div className="text-white/70">Unknown slide.</div>
        </div>
      );
  }
}

